// #tag: ADMIN PLATFORM CONFIGURATION SETTINGS SCREEN
// #purpose: Global configuration panel for system variables, exam types, student vault categories, dashboard widgets, and site branding.
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FolderLock,
  LayoutGrid,
  Settings,
  SlidersHorizontal,
} from 'lucide-react';
import {
  fetchAdminExamType,
  fetchAdminSettings,
  fetchAdminVaultCategorySetting,
  fetchAdminWidget,
  patchAdminExamType,
  patchAdminSiteConfig,
  patchAdminVaultCategorySetting,
  patchAdminWidget,
  type AdminExamTypeRow,
  type AdminSettingEntry,
  type AdminVaultCategoryRow,
  type AdminWidgetRow,
} from '../../api/admin';
import {
  AdminDetailCard,
  AdminEmptyState,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

const KIND_LABELS: Record<string, string> = {
  examType: 'Exam type',
  widget: 'Dashboard widget',
  vaultCategory: 'Vault category',
};

const THEME_COLORS = ['orange', 'blue', 'green', 'purple', 'red'];

function kindIcon(kind: string) {
  if (kind === 'widget') return LayoutGrid;
  if (kind === 'vaultCategory') return FolderLock;
  return ClipboardList;
}

type SettingSelection =
  | { kind: 'examType'; id: string; row: AdminExamTypeRow }
  | { kind: 'widget'; id: string; row: AdminWidgetRow }
  | { kind: 'vaultCategory'; id: string; row: AdminVaultCategoryRow }
  | { kind: 'platform' }
  | null;

function PlatformDetailCard({
  open,
  platform,
  onClose,
}: {
  open: boolean;
  platform: Record<string, unknown>;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const update = useMutation({
    mutationFn: (next: Record<string, unknown>) => patchAdminSiteConfig('platform', next),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-settings'] }),
  });

  const name = String(platform.name ?? '');
  const tagline = String(platform.tagline ?? '');
  const subtagline = String(platform.subtagline ?? '');
  const copyright = String(platform.copyright ?? '');
  const logoPath = String(platform.logoPath ?? '');

  return (
    <AdminDetailCard
      open={open}
      onClose={onClose}
      ariaLabel="Platform settings"
      breadcrumb="Site configuration"
      title={name || 'Platform settings'}
      illustration="admin.module.cms"
      subtitle={<span className="chip-pill">platform</span>}
    >
      <div className="wfx-admin-form">
        <label className="wfx-admin-field">
          <span>Platform name</span>
          <input
            defaultValue={name}
            onBlur={(e) => {
              const value = e.target.value.trim();
              if (value !== name) update.mutate({ ...platform, name: value });
            }}
          />
        </label>
        <label className="wfx-admin-field">
          <span>Tagline</span>
          <input
            defaultValue={tagline}
            onBlur={(e) => {
              const value = e.target.value.trim();
              if (value !== tagline) update.mutate({ ...platform, tagline: value });
            }}
          />
        </label>
        <label className="wfx-admin-field">
          <span>Subtagline</span>
          <input
            defaultValue={subtagline}
            onBlur={(e) => {
              const value = e.target.value.trim();
              if (value !== subtagline) update.mutate({ ...platform, subtagline: value });
            }}
          />
        </label>
        <label className="wfx-admin-field">
          <span>Logo path</span>
          <input
            defaultValue={logoPath}
            onBlur={(e) => {
              const value = e.target.value.trim();
              if (value !== logoPath) update.mutate({ ...platform, logoPath: value });
            }}
          />
        </label>
        <label className="wfx-admin-field">
          <span>Copyright</span>
          <input
            defaultValue={copyright}
            onBlur={(e) => {
              const value = e.target.value.trim();
              if (value !== copyright) update.mutate({ ...platform, copyright: value });
            }}
          />
        </label>
      </div>
    </AdminDetailCard>
  );
}

function SettingDetailCard({
  selection,
  onClose,
}: {
  selection: Exclude<SettingSelection, { kind: 'platform' }> | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  const examId = selection?.kind === 'examType' ? selection.id : null;
  const widgetId = selection?.kind === 'widget' ? selection.id : null;
  const vaultId = selection?.kind === 'vaultCategory' ? selection.id : null;

  const examType = useQuery({
    queryKey: ['admin-exam-type', examId],
    queryFn: () => fetchAdminExamType(examId!),
    enabled: selection?.kind === 'examType',
    initialData: selection?.kind === 'examType' ? { examType: selection.row } : undefined,
  });
  const widget = useQuery({
    queryKey: ['admin-widget', widgetId],
    queryFn: () => fetchAdminWidget(widgetId!),
    enabled: selection?.kind === 'widget',
    initialData: selection?.kind === 'widget' ? { widget: selection.row } : undefined,
  });
  const vaultCategory = useQuery({
    queryKey: ['admin-vault-category-setting', vaultId],
    queryFn: () => fetchAdminVaultCategorySetting(vaultId!),
    enabled: selection?.kind === 'vaultCategory',
    initialData: selection?.kind === 'vaultCategory' ? { vaultCategory: selection.row } : undefined,
  });

  const patchExam = useMutation({
    mutationFn: (body: Partial<AdminExamTypeRow>) => patchAdminExamType(examId!, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-settings'] });
      void qc.invalidateQueries({ queryKey: ['admin-exam-type', examId] });
    },
  });
  const patchW = useMutation({
    mutationFn: (body: Partial<AdminWidgetRow>) => patchAdminWidget(widgetId!, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-settings'] });
      void qc.invalidateQueries({ queryKey: ['admin-widget', widgetId] });
    },
  });
  const patchCat = useMutation({
    mutationFn: (body: Partial<AdminVaultCategoryRow>) => patchAdminVaultCategorySetting(vaultId!, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-settings'] });
      void qc.invalidateQueries({ queryKey: ['admin-vault-category-setting', vaultId] });
    },
  });

  if (!selection) return null;

  const examRow = selection.kind === 'examType' ? examType.data?.examType : undefined;
  const widgetRow = selection.kind === 'widget' ? widget.data?.widget : undefined;
  const vaultRow = selection.kind === 'vaultCategory' ? vaultCategory.data?.vaultCategory : undefined;
  const title = examRow?.name ?? vaultRow?.label ?? widgetRow?.title ?? widgetRow?.widgetType ?? 'Setting';
  const activeQuery = selection.kind === 'examType' ? examType : selection.kind === 'widget' ? widget : vaultCategory;
  const detailError = activeQuery.isError;

  return (
    <AdminDetailCard
      open
      onClose={onClose}
      ariaLabel="Setting details"
      breadcrumb={KIND_LABELS[selection.kind] ?? 'Configuration'}
      title={title}
      illustration="admin.module.cms"
      subtitle={<><span className="chip-pill">{KIND_LABELS[selection.kind]}</span><AdminStatusBadge status={examRow ? (examRow.isActive ? 'active' : 'disabled') : widgetRow ? (widgetRow.isEnabled ? 'enabled' : 'disabled') : vaultRow ? (vaultRow.isActive ? 'active' : 'disabled') : 'active'} /></>}
    >
      {detailError && <ErrorState message="Could not load this setting." onRetry={() => void activeQuery.refetch()} />}

      {examRow && (
        <>
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>Slug</dt><dd>{examRow.slug}</dd></div>
            <div><dt>Display order</dt><dd>{examRow.displayOrder}</dd></div>
            <div><dt>Theme</dt><dd>{examRow.themeColor ?? '—'}</dd></div>
            <div><dt>Icon</dt><dd>{examRow.icon ?? '—'}</dd></div>
          </dl>
          <div className="wfx-admin-drawer-columns">
            <div className="wfx-admin-drawer-section">
              <h3>Identity</h3>
              <div className="wfx-admin-form">
                <label className="wfx-admin-field">
                  <span>Name</span>
                  <input defaultValue={examRow.name} onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== examRow.name) patchExam.mutate({ name: v }); }} />
                </label>
                <label className="wfx-admin-field">
                  <span>Theme color</span>
                  <select defaultValue={examRow.themeColor ?? 'orange'} onChange={(e) => patchExam.mutate({ themeColor: e.target.value })}>
                    {THEME_COLORS.map((color) => <option key={color} value={color}>{color}</option>)}
                  </select>
                </label>
                <label className="wfx-admin-field">
                  <span>Display order</span>
                  <input type="number" defaultValue={examRow.displayOrder} onBlur={(e) => { const v = Number(e.target.value) || 0; if (v !== examRow.displayOrder) patchExam.mutate({ displayOrder: v }); }} />
                </label>
              </div>
            </div>
            <div className="wfx-admin-drawer-section">
              <h3>Capabilities</h3>
              <div className="wfx-admin-form">
                <button type="button" className={`chip-pill${examRow.isActive ? ' is-active' : ''}`} onClick={() => patchExam.mutate({ isActive: !examRow.isActive })}>
                  {examRow.isActive ? 'Active' : 'Inactive'}
                </button>
                <p className="wfx-muted wfx-admin-subline">
                  Results: {examRow.supportsResults ? 'Yes' : 'No'} · Admit cards: {examRow.supportsAdmitCards ? 'Yes' : 'No'}
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {widgetRow && (
        <>
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>Widget type</dt><dd>{widgetRow.widgetType}</dd></div>
            <div><dt>Visibility</dt><dd>{widgetRow.visibility}</dd></div>
            <div><dt>Display order</dt><dd>{widgetRow.displayOrder}</dd></div>
          </dl>
          <div className="wfx-admin-form">
            <label className="wfx-admin-field">
              <span>Title</span>
              <input defaultValue={widgetRow.title ?? ''} onBlur={(e) => { const v = e.target.value.trim(); if (v !== (widgetRow.title ?? '')) patchW.mutate({ title: v || null }); }} />
            </label>
            <label className="wfx-admin-field">
              <span>Visibility</span>
              <select defaultValue={widgetRow.visibility} onChange={(e) => patchW.mutate({ visibility: e.target.value })}>
                <option value="authenticated">Authenticated</option>
                <option value="public">Public</option>
              </select>
            </label>
            <label className="wfx-admin-field">
              <span>Display order</span>
              <input type="number" defaultValue={widgetRow.displayOrder} onBlur={(e) => { const v = Number(e.target.value) || 0; if (v !== widgetRow.displayOrder) patchW.mutate({ displayOrder: v }); }} />
            </label>
            <button type="button" className={`chip-pill${widgetRow.isEnabled ? ' is-active' : ''}`} onClick={() => patchW.mutate({ isEnabled: !widgetRow.isEnabled })}>
              {widgetRow.isEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>
        </>
      )}

      {vaultRow && (
        <>
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>Icon</dt><dd>{vaultRow.icon}</dd></div>
            <div><dt>Subcategories</dt><dd>{vaultRow.itemCount ?? vaultRow.items?.length ?? 0}</dd></div>
            <div><dt>Display order</dt><dd>{vaultRow.displayOrder}</dd></div>
          </dl>
          <div className="wfx-admin-form">
            <label className="wfx-admin-field">
              <span>Label</span>
              <input defaultValue={vaultRow.label} onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== vaultRow.label) patchCat.mutate({ label: v }); }} />
            </label>
            <label className="wfx-admin-field">
              <span>Display order</span>
              <input type="number" defaultValue={vaultRow.displayOrder} onBlur={(e) => { const v = Number(e.target.value) || 0; if (v !== vaultRow.displayOrder) patchCat.mutate({ displayOrder: v }); }} />
            </label>
            <button type="button" className={`chip-pill${vaultRow.isActive ? ' is-active' : ''}`} onClick={() => patchCat.mutate({ isActive: !vaultRow.isActive })}>
              {vaultRow.isActive ? 'Active' : 'Inactive'}
            </button>
            {vaultRow.items && vaultRow.items.length > 0 && (
              <div className="wfx-admin-tag-row">
                {vaultRow.items.slice(0, 8).map((item) => <span key={item} className="chip-pill">{item}</span>)}
              </div>
            )}
          </div>
        </>
      )}
    </AdminDetailCard>
  );
}

function SettingCard({ row, onSelect }: { row: AdminSettingEntry; onSelect: () => void }) {
  const Icon = kindIcon(row.kind);
  return (
    <button type="button" className="wfx-admin-settings-card glass-surface" onClick={onSelect}>
      <div className="wfx-admin-media-card-icon"><Icon size={24} aria-hidden="true" /></div>
      <div className="wfx-admin-page-card-copy">
        <strong>{row.title}</strong>
        <span className="wfx-muted">{KIND_LABELS[row.kind]} · {row.subtitle}</span>
      </div>
      <div className="wfx-admin-page-card-stats">
        <AdminStatusBadge status={row.status} />
        <span>Order {row.displayOrder}</span>
      </div>
    </button>
  );
}

function entryToSelection(entry: AdminSettingEntry): SettingSelection {
  if (entry.kind === 'examType' && entry.examType) return { kind: 'examType', id: entry.id, row: entry.examType };
  if (entry.kind === 'widget' && entry.widget) return { kind: 'widget', id: entry.id, row: entry.widget };
  if (entry.kind === 'vaultCategory' && entry.vaultCategory) return { kind: 'vaultCategory', id: entry.id, row: entry.vaultCategory };
  return null;
}

export function AdminSettingsScreen() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [kindFilter, setKindFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selection, setSelection] = useState<SettingSelection>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, kindFilter, statusFilter]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-settings', debouncedQuery, kindFilter, statusFilter, page],
    queryFn: () => fetchAdminSettings({
      q: debouncedQuery || undefined,
      kind: kindFilter,
      status: statusFilter,
      page,
      limit: 12,
    }),
  });

  const rows = data?.data ?? [];
  const stats = data?.stats;
  const kinds = data?.kinds ?? [];
  const pagination = data?.pagination;
  const platform = (data?.platform ?? {}) as Record<string, unknown>;

  const kpiItems = useMemo(() => [
    { label: 'Total items', value: stats?.total ?? 0 },
    { label: 'Exam types', value: `${stats?.activeExamTypes ?? 0}/${stats?.examTypes ?? 0}` },
    { label: 'Widgets', value: `${stats?.enabledWidgets ?? 0}/${stats?.widgets ?? 0}` },
    { label: 'Vault categories', value: `${stats?.activeVaultCategories ?? 0}/${stats?.vaultCategories ?? 0}` },
  ], [stats]);

  if (isLoading && !data) return <LoadingState label="Loading settings…" />;
  if (isError) return <ErrorState message="Could not load settings." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout
        breadcrumb="Admin / Settings"
        title="Settings"
        description="Configure platform branding, exam types, dashboard widgets, and vault taxonomy."
        icon={Settings}
        illustration="admin.module.cms"
        actions={(
          <button type="button" className="secondary-action compact" onClick={() => setSelection({ kind: 'platform' })}>
            <SlidersHorizontal size={16} aria-hidden="true" />
            Platform config
          </button>
        )}
      >
        <AdminKpiStrip items={kpiItems} />

        <div className="wfx-admin-blog-grid">
          {rows.map((row) => (
            <SettingCard key={`${row.kind}-${row.id}`} row={row} onSelect={() => setSelection(entryToSelection(row))} />
          ))}
        </div>
        {rows.length === 0 && <AdminEmptyState message="No settings match your filters." />}

        <AdminTablePanel
          title="Configuration directory"
          toolbar={(
            <div className="wfx-admin-toolbar">
              <AdminSearchInput value={query} onChange={setQuery} placeholder="Search name, slug, or type…" />
              <select className="wfx-admin-select" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} aria-label="Filter by kind">
                <option value="all">All kinds</option>
                {kinds.map((kind) => <option key={kind} value={kind}>{KIND_LABELS[kind] ?? kind}</option>)}
              </select>
              <select className="wfx-admin-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter by status">
                <option value="all">All status</option>
                <option value="active">Active / enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
          )}
          footer={
            pagination && pagination.pages > 1 ? (
              <div className="wfx-admin-pagination">
                <button type="button" className="secondary-action compact" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeft size={16} aria-hidden="true" />
                  Previous
                </button>
                <span className="wfx-muted">
                  Page {pagination.page} of {pagination.pages} · {pagination.total} items
                </span>
                <button type="button" className="secondary-action compact" disabled={page >= pagination.pages} onClick={() => setPage((p) => p + 1)}>
                  Next
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            ) : undefined
          }
        >
          <table className="wfx-admin-table">
            <thead>
              <tr><th>Name</th><th>Kind</th><th>Details</th><th>Order</th><th>Status</th></tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.kind}-${row.id}`} className="wfx-admin-table-row-clickable" onClick={() => setSelection(entryToSelection(row))}>
                  <td><strong>{row.title}</strong></td>
                  <td>{KIND_LABELS[row.kind] ?? row.kind}</td>
                  <td>{row.subtitle}</td>
                  <td>{row.displayOrder}</td>
                  <td><AdminStatusBadge status={row.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTablePanel>
      </AdminPageLayout>

      <SettingDetailCard
        selection={selection && selection.kind !== 'platform' ? selection : null}
        onClose={() => setSelection(null)}
      />
      <PlatformDetailCard open={selection?.kind === 'platform'} platform={platform} onClose={() => setSelection(null)} />
    </>
  );
}
